import { proxy, subscribe } from 'valtio';

import { componentInstanceValidator } from '@diagram/validators/component-instances/component-instance-validator';
import { faultZoneValidator } from '@diagram/validators/zones/fault-zone-validator';
import { formatters } from '@diagram/validators/zones/formatters';
import { galvanicZoneValidator } from '@diagram/validators/zones/galvanic-zone-validator';

import {
    ComponentInstanceValidationError,
    FeatureFlags,
    ValidationCount,
    ValidationFeature,
    ZoneValidationError,
} from 'models';
import { FeatureFlagHelpers } from 'helpers/FeatureFlagHelpers';

import { state as currentProjectState } from 'state/current-project';
import { state as currentUserState } from 'state/current-user';

import { controlZoneValidator } from '../validators/zones/control-zone-validator';
import { diagram } from './diagram';
import { zonesState } from './zones';

export const diagramValidationState = proxy<{
    errors: {
        zones: {
            control: ZoneValidationError[];
            currentOS: ZoneValidationError[];
            galvanic: ZoneValidationError[];
            fault: ZoneValidationError[];
            protection: ZoneValidationError[];
        };
        componentInstances: ComponentInstanceValidationError[];
    };
    count: Record<ValidationFeature | 'total', ValidationCount>;
}>({
    errors: {
        zones: {
            control: [],
            currentOS: [],
            galvanic: [],
            fault: [],
            protection: [],
        },
        componentInstances: [],
    },
    count: {
        simulations: { all: 0, errors: 0, warnings: 0 },
        wireSizing: { all: 0, errors: 0, warnings: 0 },
        fault: { all: 0, errors: 0, warnings: 0 },
        total: { all: 0, errors: 0, warnings: 0 },
    },
});

subscribe(diagramValidationState.errors, () => {
    const counts: Record<ValidationFeature | 'total', ValidationCount> = {
        simulations: { all: 0, errors: 0, warnings: 0 },
        wireSizing: { all: 0, errors: 0, warnings: 0 },
        fault: { all: 0, errors: 0, warnings: 0 },
        total: { all: 0, errors: 0, warnings: 0 },
    };

    const count = (bucket: ValidationCount, level: 'error' | 'warning') => {
        bucket.all++;
        bucket[level === 'error' ? 'errors' : 'warnings']++;
    };

    const zoneErrors = [
        ...diagramValidationState.errors.zones.control,
        ...diagramValidationState.errors.zones.currentOS,
        ...diagramValidationState.errors.zones.galvanic,
        ...diagramValidationState.errors.zones.fault,
    ];

    zoneErrors.forEach((error) => {
        count(counts.total, error.level);

        const features = formatters[error.type](error).features ?? {};

        Object.entries(features).forEach(([key, value]) => {
            if (value) {
                count(counts[key as ValidationFeature], error.level);
            }
        });
    });

    diagramValidationState.errors.componentInstances.forEach((error) => {
        count(counts.total, error.level);

        const features = componentInstanceValidator.formatters[error.type](error).features ?? {};

        Object.entries(features).forEach(([key, value]) => {
            if (value) {
                count(counts[key as ValidationFeature], error.level);
            }
        });
    });

    diagramValidationState.count = counts;
});

subscribe(diagram, () => {
    const showStateOfChargeDerating = FeatureFlagHelpers.isEnabledForProject(FeatureFlags.STATE_OF_CHARGE_DERATING, {
        user: currentUserState.user,
        project: currentProjectState.project,
    });

    diagramValidationState.errors.componentInstances = componentInstanceValidator
        .validator({ diagram })
        .filter((error) => showStateOfChargeDerating || !error.type.includes('STATE_OF_CHARGE_DERATING'));
});

subscribe(zonesState, () => {
    diagramValidationState.errors.zones.control = controlZoneValidator({ diagram, zones: zonesState.zones.control });
    diagramValidationState.errors.zones.galvanic = galvanicZoneValidator({ diagram, zones: zonesState.zones.galvanic });
    diagramValidationState.errors.zones.fault = faultZoneValidator({
        diagram,
        zones: zonesState.zones.fault,
        controlZones: zonesState.zones.control,
    });
});
